/**
 * Pure utility functions for provider enrichment value resolution.
 * These functions determine which value to display based on the hierarchy:
 * organization value > provider value > enrichment value
 */

import type { NaceCode, Provider, ProviderEnrichment } from '@app/src/types/organizations'
import { ProviderEnrichmentStatus } from '@app/src/types/organizations'

export type EnrichedValue<T> = {
  value: T | undefined | null
  isEnriched: boolean
  enrichmentValue: T | undefined | null
}

type MaybeValue<T> = T | undefined | null

function hasValue<T>(value: MaybeValue<T>): value is T {
  if (value === undefined || value === null) return false
  if (typeof value !== 'string') return true
  const trimmed = value.trim()
  return trimmed !== '' && trimmed.toLowerCase() !== 'null'
}

export function getEnrichedValue<T>(
  orgValue: MaybeValue<T>,
  providerValue: MaybeValue<T>,
  enrichmentValues: MaybeValue<T>[],
): EnrichedValue<T> {
  const enrichmentValue = enrichmentValues.find(hasValue)

  if (hasValue(orgValue)) {
    return { value: orgValue, isEnriched: false, enrichmentValue }
  }
  if (hasValue(providerValue)) {
    return { value: providerValue, isEnriched: enrichmentValues.includes(providerValue), enrichmentValue }
  }
  return { value: enrichmentValue, isEnriched: enrichmentValue !== undefined, enrichmentValue }
}

/**
 * A provider can be enriched unless an enrichment is currently running, or it already has a
 * completed one (and multiple enrichments are not allowed). Failed enrichments never block,
 * so the user can always retry after a failure.
 */
export function canProviderBeEnriched(provider: Provider, allowMultiple: boolean): boolean {
  const enrichments = provider.enrichments ?? []
  const hasInProgressEnrichment = enrichments.some(e => e.status === ProviderEnrichmentStatus.InProgress)
  if (hasInProgressEnrichment) {
    return false
  }

  const hasCompletedEnrichment = enrichments.some(e => e.status === ProviderEnrichmentStatus.Completed)
  return allowMultiple || !hasCompletedEnrichment
}

export function getCompletedEnrichmentsLatestFirst(provider: Provider): ProviderEnrichment[] {
  const completedAtTime = (enrichment: ProviderEnrichment) =>
    enrichment.completedAt ? new Date(enrichment.completedAt).getTime() : 0

  return (provider.enrichments ?? [])
    .filter(e => e.status === ProviderEnrichmentStatus.Completed)
    .sort((a, b) => completedAtTime(b) - completedAtTime(a))
}

/**
 * Resolves the effective sub-industry NaceCode for a provider.
 * Priority: organization > provider
 *
 * Note: Enrichment data is now synced to provider.subIndustry automatically,
 * so no need to check enrichments separately.
 */
export function resolveSubIndustry(provider: Provider): NaceCode | undefined {
  return provider.organization?.subIndustryNaceCode ?? provider.subIndustry
}

/**
 * Gets the effective website address for a provider.
 * Priority: provider.websiteAddress (which includes synced enrichment data)
 *
 * Note: Enrichment data is now synced to provider.websiteAddress automatically,
 * so no need to check enrichments separately.
 */
export function resolveProviderWebsiteAddress(provider: Provider): string | undefined {
  return provider.websiteAddress
}
